import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Icon from './Icon';
import { getAccessToken, isSecretaryContextUser } from '../authStorage';
import { fetchBranches } from '../branchesApi';
import { createDecisionType, fetchDecisionTypeById, mapDecisionTypeFormToPayload, updateDecisionType } from '../decisionTypesApi';
import { EMPTY_DECISION_TYPE_FORM, getSampleDecisionTypeForm, validateDecisionTypeForm } from '../decisionTypeFormUtils';
import { useLanguage } from '../i18n/language';
import { useT } from '../i18n/translations';
import './BranchesPage.css';
import './BoardPage.css';

function DecisionTypeFormPage({ mode }) {
  const { direction } = useLanguage();
  const t = useT();
  const navigate = useNavigate();
  const { id: idParam } = useParams();
  const isNew = mode === 'new';
  const editId = isNew ? null : Number(idParam);
  const invalidEditId = !isNew && (!Number.isFinite(editId) || editId < 1);
  const [branches, setBranches] = useState([]);
  const [form, setForm] = useState(EMPTY_DECISION_TYPE_FORM);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [accessDenied, setAccessDenied] = useState(false);

  useEffect(() => {
    if (!getAccessToken()) { navigate('/login', { replace: true }); return; }
    if (!isSecretaryContextUser()) { setAccessDenied(true); setLoading(false); return; }
    if (invalidEditId) { setLoadError(t('decisionTypes.invalidId')); setLoading(false); return; }
    let cancelled = false;
    (async () => {
      setLoading(true); setLoadError('');
      try {
        const branchRows = await fetchBranches();
        if (cancelled) return;
        setBranches(branchRows);
        const firstBranchId = branchRows[0]?.id ? String(branchRows[0].id) : '';
        if (isNew) {
          setForm({ ...EMPTY_DECISION_TYPE_FORM, branchId: firstBranchId });
        } else {
          const item = await fetchDecisionTypeById(editId);
          if (cancelled) return;
          setForm({ name: item.name, description: item.description || '', branchId: String(item.branchId), status: item.status });
        }
      } catch (e) {
        if (!cancelled) setLoadError(e instanceof Error ? e.message : t('common.errorLoad'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [navigate, isNew, editId, invalidEditId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (e) => {
    setErrors((p) => ({ ...p, [e.target.name]: '', _api: '' }));
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));
  };

  const handleFillSample = useCallback(() => {
    if (!isNew) return;
    const branchId = form.branchId || (branches[0] && String(branches[0].id)) || '';
    setErrors({});
    setForm(getSampleDecisionTypeForm(branchId));
  }, [isNew, form.branchId, branches]);

  const handleSave = async () => {
    const errs = validateDecisionTypeForm(form, {
      nameRequired: t('decisionTypes.valName'),
      branchRequired: t('decisionTypes.valBranch'),
    });
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSaving(true);
    try {
      const payload = mapDecisionTypeFormToPayload(form);
      if (isNew) await createDecisionType(payload);
      else await updateDecisionType(editId, payload);
      navigate('/app/decision-types');
    } catch (e) {
      setErrors({ _api: e instanceof Error ? e.message : t('decisionTypes.saveFailed') });
    } finally {
      setSaving(false);
    }
  };

  if (accessDenied) return <p style={{ padding: 24, color: '#78909c', fontFamily: 'Cairo, sans-serif' }}>{t('common.noPerm')}</p>;

  return (
    <div className="page" dir={direction}>
      <div className="page__topbar">
        <div className="page__topbar-left">
          <button type="button" className="btn btn--ghost" onClick={() => navigate('/app/decision-types')} style={{ marginInlineEnd: 8 }}>
            <Icon name="chevron-right" size={14} /> {t('common.back')}
          </button>
          <span className="page__topbar-title"><Icon name="decisions" size={18} color="#00334a" style={{ verticalAlign: 'middle', marginLeft: 6 }} />{isNew ? t('decisionTypes.titleNew') : t('decisionTypes.titleEdit')}</span>
          <span className="page__topbar-sub">{t('decisionTypes.subtitleForm')}</span>
        </div>
        <div className="page__topbar-actions" style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          {isNew && (
            <button type="button" className="btn btn--ghost" onClick={handleFillSample} disabled={saving || loading}>
              {t('decisionTypes.fillSample')}
            </button>
          )}
          <button type="button" className="btn btn--ghost" onClick={() => navigate('/app/decision-types')}>{t('common.cancel')}</button>
          <button type="button" className="btn btn--primary" onClick={handleSave} disabled={saving || loading || !!loadError}>
            {saving ? t('common.loading') : isNew ? t('decisionTypes.submitNew') : t('decisionTypes.submitEdit')}
          </button>
        </div>
      </div>
      {loadError && <div style={{ marginBottom: 12, padding: '10px 14px', background: '#ffebee', color: '#c62828', borderRadius: 10 }}>{loadError}</div>}
      {loading && <div style={{ padding: 24, textAlign: 'center', color: '#78909c' }}>{t('common.loading')}</div>}
      {!loading && !loadError && (
        <div className="table-card" style={{ padding: '20px 22px' }}>
          <div className="mform">
            {errors._api && <div style={{ marginBottom: 12, padding: '10px 12px', background: '#ffebee', color: '#c62828', borderRadius: 8 }}>{errors._api}</div>}
            <div className="mform__row mform__row--4">
              <div className="mform__field">
                <label className="mform__label">{t('decisionTypes.lblName')} <span>*</span></label>
                <div className="mform__input-wrap"><Icon name="decisions" size={14} color="#02c3a6" className="mform__input-icon" /><input className={`mform__input mform__input--with-icon${errors.name ? ' mform__input--error' : ''}`} name="name" value={form.name} onChange={handleChange} /></div>
                {errors.name && <span className="mform__error">{errors.name}</span>}
              </div>
              <div className="mform__field mform__field--span3">
                <label className="mform__label">{t('decisionTypes.lblBranch')} <span>*</span></label>
                <div className="mform__input-wrap"><Icon name="branch" size={14} color="#1b8eff" className="mform__input-icon" /><select className={`mform__select mform__input--with-icon${errors.branchId ? ' mform__select--error' : ''}`} name="branchId" value={form.branchId} onChange={handleChange}><option value="">{t('decisionTypes.pickBranch')}</option>{branches.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></div>
                {errors.branchId && <span className="mform__error">{errors.branchId}</span>}
              </div>
            </div>
            <div className="mform__row mform__row--4">
              <div className="mform__field mform__field--span3">
                <label className="mform__label">{t('decisionTypes.lblDescription')}</label>
                <div className="mform__input-wrap"><Icon name="report" size={14} color="#8b5cf6" className="mform__input-icon" /><input className="mform__input mform__input--with-icon" name="description" value={form.description} onChange={handleChange} /></div>
              </div>
              <div className="mform__field">
                <label className="mform__label">{t('decisionTypes.lblStatus')}</label>
                <div className="mform__input-wrap"><Icon name="settings" size={14} color="#14b8a6" className="mform__input-icon" /><select className="mform__select mform__input--with-icon" name="status" value={form.status} onChange={handleChange}><option value="active">{t('decisionTypes.statusActive')}</option><option value="inactive">{t('decisionTypes.statusInactive')}</option></select></div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DecisionTypeFormPage;
